import { put, call, cancel, select, takeLatest } from 'redux-saga/effects';
import size from 'lodash/size';

import * as inspectReq from '../requests/inspection-req';
import * as visitTypes from '../requests/visit-types-req';
import { hideLoaderInPopup } from '../../ui/popup';
import * as publixInfo from '../requests/publix-info-req';

import {
  redirectToServices,
  redirectToLocations,
  redirectToPublixInfo,
} from '../../../utils/appointments/redirect-utils';
import {
  INLINE_TYPES,
  APPOINTMENT_ALLOWED_TYPES,
  APPOINTMENT_HIDDEN_TYPES,
  APPOINTMENT_INSPECTION,
  PUBLIX_CODE,
} from '../../../constants';
import {
  APPOINTMENT_PROGRESS_TYPES,
  appointmentProgressActions,
} from '../progress-types';
import {
  selectProgressCareUser,
  selectProgramBasedVisitId,
  selectProgressOrganization,
} from '../selectors';

const [
  URGENT_CARE_TYPE_CODE,
  IMAGING_TYPE_CODE,
  LAB_TYPE_CODE,
  TECH_CONNECT_CODE,
] = INLINE_TYPES;

const { PROFILE, DOCUMENTS } = APPOINTMENT_INSPECTION;

const getNotHiddenVisitTypes = types =>
  types.filter(({ code }) => !APPOINTMENT_HIDDEN_TYPES.includes(code));

function* servicesListGuard(services) {
  if (size(services) === 1) {
    yield put(appointmentProgressActions.setService(services[0]));
    const { id: userId } = yield select(selectProgressCareUser);
    yield put(
      inspectReq.actions.send({
        userId,
        inspectionFor: [PROFILE, DOCUMENTS],
      }),
    );
  } else yield call(redirectToServices);
}

function* handleCheckVisitType(visit) {
  const { code } = visit;
  const isAllowedType = APPOINTMENT_ALLOWED_TYPES.includes(code);

  if (isAllowedType) {
    yield put(appointmentProgressActions.selectVisitType(visit));
  }
}

function* handleVisitTypeSelect({ payload }) {
  // when user select visit type - go to service
  yield put(appointmentProgressActions.setVisitType(payload));
  const { code, services } = payload;
  switch (code) {
    case URGENT_CARE_TYPE_CODE:
    case IMAGING_TYPE_CODE:
    case LAB_TYPE_CODE:
    case TECH_CONNECT_CODE: {
      yield put(appointmentProgressActions.setService(services[0]));
      return yield call(redirectToLocations);
    }
    case PUBLIX_CODE: {
      yield put(appointmentProgressActions.setService(services[0]));
      yield put(publixInfo.actions.send({ visitCode: code }));
      return yield call(redirectToPublixInfo);
    }
    default:
      return yield call(servicesListGuard, services);
  }
}

function* handleProgramBasedVisitTypeSelect({ payload }) {
  yield put(appointmentProgressActions.setProgramBasedVisitId(payload));

  const notHiddenVisitTypeList = getNotHiddenVisitTypes(payload.visitTypes);
  const listSize = size(notHiddenVisitTypeList);

  if (listSize === 1) {
    yield call(handleCheckVisitType, notHiddenVisitTypeList[0]);
  }
}

function* fetchVisits() {
  const programBasedVisitId = yield select(selectProgramBasedVisitId);

  if (programBasedVisitId) {
    yield put(appointmentProgressActions.clearProgramBasedVisitId());
  }

  const { visitTypes: visitTypesList, pbvConfigs } = yield select(
    selectProgressOrganization,
  );

  const safeVisitTypesList = getNotHiddenVisitTypes(visitTypesList);
  const commonVisitsSize = size([...pbvConfigs, ...safeVisitTypesList]);
  const isOnlyOneVisit = commonVisitsSize === 1;

  if (isOnlyOneVisit && size(pbvConfigs) === 1) {
    yield put(
      appointmentProgressActions.selectProgramBasedVisit(pbvConfigs[0]),
    );
    yield cancel();
  }

  if (isOnlyOneVisit && size(visitTypesList) === 1) {
    yield call(handleCheckVisitType, visitTypesList[0]);
  }

  if (commonVisitsSize >= 0) {
    yield put(hideLoaderInPopup());
  }
}

export default function* organizationListGuard() {
  yield takeLatest(visitTypes.consts.INIT, fetchVisits);

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.SELECT_VISIT_TYPE,
    handleVisitTypeSelect,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.SELECT_PROGRAM_BASED_VISIT,
    handleProgramBasedVisitTypeSelect,
  );
}



// WEBPACK FOOTER //
// ./src/redux/appointmentForm/sagas/visit-type-list-guard-saga.js